/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Edit, Delete } from "@mui/icons-material";
import { Box, Typography, IconButton } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { IShift } from "../../../../../interfaces/ShiftInterface";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { formatTime12hr } from "../../../../../helpers/formatters";
import { useDeleteShift } from "../../../../../hooks/query/useShifts";

interface ShiftInfoPanelProps {
  shift: IShift;
  onClose: () => void;
  onEdit: (shift: IShift) => void;
}

function DetailRow({ label, value, sx }: { label: string; value: string | number; sx?: any }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline" }}>
      <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
        {label}
      </Typography>
      <Typography sx={sx}>{value}</Typography>
    </Box>
  );
}

// eslint-disable-next-line import/prefer-default-export
export function ShiftInfoPanel({ shift, onClose, onEdit }: ShiftInfoPanelProps): React.JSX.Element {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deleteShiftMutation } = useDeleteShift();

  const handleDelete = () => {
    deleteShiftMutation(shift.id as number, {
      onSuccess: (response) => {
        if (response.success) {
          onClose();
        }
      }
    });
  };

  const titleJsx = (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      pb={1}
    >
      <Box display="flex" sx={{ alignItems: "center" }}>
        <Typography>
          <Box component="span" fontWeight="600" fontSize="16px" pr={2}>
            Shift Information
          </Box>
        </Typography>
      </Box>
      <Box display="flex">
        <IconButton
          sx={{ cursor: "pointer", padding: "5px" }}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Delete />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "baseline" }} ml="8px">
          <IconButton sx={{ cursor: "pointer", padding: "5px" }} onClick={() => onEdit(shift)}>
            <Edit />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Panel titleJsx={titleJsx} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <DetailRow label="Shift Name" value={shift.name} />
        <DetailRow label="Shift Code" value={shift.shiftCode} />
        <DetailRow label="Start Time" value={formatTime12hr(shift.startTime)} />
        <DetailRow label="End Time" value={formatTime12hr(shift.endTime)} />
        <DetailRow label="Break Start" value={formatTime12hr(shift.breakStartTime)} />
        <DetailRow label="Break End" value={formatTime12hr(shift.breakEndTime)} />
        <DetailRow label="Grace In" value={`${shift.graceInMinutes || 0} minutes`} />
        <DetailRow label="Grace Out" value={`${shift.graceOutMinutes || 0} minutes`} />
        <DetailRow label="Overnight" value={shift.isOvernight ? "Yes" : "No"} />
        <DetailRow
          label="Status"
          value={shift.status}
          sx={{
            color: shift.status === "active" ? "success.main" : "error.main",
            textTransform: "capitalize"
          }}
        />
      </Box>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Shift"
          description={`Are you sure you want to delete the shift "${shift.name}"?`}
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}
    </Panel>
  );
}
